import { useEffect, useState, useMemo } from "react";
import { API_STATUS } from "../../constants";
import { GetUserPosts } from "../../apis/posts/get_user_post";

export default function ArtistMedia({ setPostCount, userId, activeTab }) {
    const [postApiStatus, setPostApiStatus] = useState(API_STATUS);

    useEffect(() => {
        GetUserPosts(userId, setPostApiStatus);
    }, [userId]);

    useEffect(() => {
        if (!postApiStatus.isError && !postApiStatus.isLoader && postApiStatus.data != null) {
            let res = postApiStatus.data;
            if (res['status'] === 1) {
                setPostCount(res['data']?.length || 0);
            }
        }
    }, [postApiStatus, setPostCount]);

    // Flatten posts into individual media items with parent post context
    const filteredMedia = useMemo(() => {
        if (!postApiStatus.data?.data) return [];

        const posts = postApiStatus.data.data;

        return posts.flatMap(post => 
            (post.media_items || []).map(media => ({
                ...media,
                p_id: post.p_id,
                p_title: post.p_title
            }))
        ).filter(media => {
            const fileType = media.file_type?.toLowerCase() || "";

            if (activeTab === "post_image") {
                return fileType === "photo" || fileType === "photos" || fileType === "image";
            }
            if (activeTab === "post_video") {
                return fileType === "video" || fileType === "videos";
            }

            return true;
        });
    }, [postApiStatus.data, activeTab]);

    if (postApiStatus.isLoader) {
        return <div className="text-center py-10">Loading media...</div>;
    }

    if (postApiStatus.isError) {
        return <div className="text-center py-10 text-red-500">Failed to load media.</div>;
    }

    return (
        <div className="mt-5">
            {filteredMedia.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                    No media found.
                </div>
            ) : (
                <div className="grid grid-cols-6 gap-1 md:gap-4">
                    {filteredMedia.map((item) => {
                        const displayImage = item.thumbnail || item.file_path;
                        const isVideo = item.file_type?.toLowerCase().startsWith("video");

                        return (
                            <a
                                key={item.file_id}
                                href={'/post_id/'+item.p_id}
                                rel="noopener noreferrer"
                                className="relative group aspect-square overflow-hidden bg-gray-100 block"
                            >
                                <img
                                    src={displayImage}
                                    alt={item.p_title || "User media"}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />

                                {/* Center Play Icon for Videos */}
                                {isVideo && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="bg-black/60 backdrop-blur-sm rounded-full p-3 text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                className="w-6 h-6 md:w-8 md:h-8 translate-x-0.5" 
                                                fill="currentColor" 
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </div>
                                )}

                                {/* Hover Overlay for Title */}
                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end p-2 text-white">
                                    <span className="text-xs md:text-sm font-semibold truncate w-full">
                                        {item.p_title}
                                    </span>
                                </div>
                            </a>
                        );
                    })}
                </div>
            )}
        </div>
    );
}